'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type { GameMeta } from './games';
import type { GameType } from './types';

/*
  Player settings: the state and the rules, with the storage injected.

  Same shape as TimerProvider — the logic is portable, the platform bit is a prop. The web backs it
  with localStorage; a native client backs it with async storage. `load` and `save` may return either
  a value or a promise so both fit without the web paying for machinery it doesn't need.

  Settings are a single JSON blob, global keys at the top level and per-game keys under `games`,
  which is the shape already on disk in browsers. Changing that would strand every existing player's
  preferences.
*/

/**
 * The settings a player can change. Keys are the strings already persisted in browsers, so renaming
 * one silently resets that preference for everyone who has it set.
 */
export const SettingsKeys = {
  SHOW_TIMER: 'showTimerByDefault',
  SHARE_INCLUDES_URL: 'shareUrl',
  ENABLE_SWIPE: 'enableSwipe',
  DISABLE_AUTO_PAUSE: 'disableAutoPause',
  HIDE_HINT_BUTTONS: 'hideHintButtons',
  DISABLE_AUTO_CHECK: 'disableAutoCheck',
  CHECKERBOARD: 'checkerboard',
  /** Mosaic: cross out the rest of a line once its clue is fully ticked off and its fills fit the clue. */
  AUTO_CROSS_LINES: 'autoCrossCompletedLines',
  /** Mosaic: tick off a line's clue once it has no empty cells and its fills fit the clue. */
  AUTO_COMPLETE_CLUES: 'autoCompleteClues',
} as const;

export type SettingKey = (typeof SettingsKeys)[keyof typeof SettingsKeys];

/** Everything a client needs to offer and read one setting. */
export interface SettingInfo {
  /** What the setting is called wherever a player can change it. See `settingName` for the one exception. */
  name: string;
  /** The longer explanation shown beside the name, for the settings whose name needs one. */
  description?: string;
  /** What the setting means before the player has ever touched it. */
  defaultValue: boolean;
  /** Show the toggle on when the stored value is *false*, for a "Disable X" name over an ENABLE_X key. */
  invert?: boolean;
  /** Whether the setting does anything in this game, and so whether its settings screen offers it. */
  enabledFor: (game: GameType) => boolean;
}

const everyGame = () => true;

export const SettingsInfo: Record<SettingKey, SettingInfo> = {
  showTimerByDefault: { name: 'Show timer', defaultValue: false, enabledFor: everyGame },
  shareUrl: { name: 'Include site link', defaultValue: true, enabledFor: everyGame },
  // Loopy River is drawn rather than tapped, so there's no swipe to disable.
  enableSwipe: {
    name: 'Disable swipe',
    defaultValue: true,
    invert: true,
    enabledFor: (game) => game !== 'loopy-river',
  },
  disableAutoPause: {
    name: 'Disable auto-pause',
    description: 'Prevent the timer from pausing when the window loses focus',
    defaultValue: false,
    enabledFor: everyGame,
  },
  hideHintButtons: { name: 'Hide hint and check buttons', defaultValue: false, enabledFor: everyGame },
  disableAutoCheck: {
    name: 'Disable auto-check',
    description: 'Disables the automatic check that is performed when every cell of the board is filled.',
    defaultValue: false,
    // A Mosaic board is either already complete or simply unfinished, so it never auto-checks.
    enabledFor: (game) => game !== 'mosaic',
  },
  checkerboard: { name: 'Checkerboard grid', defaultValue: false, enabledFor: (game) => game === 'loopy-river' },
  autoCrossCompletedLines: {
    name: 'Auto-X completed lines',
    description:
      'When you tick off every number in a clue and the filled cells in that line match it, the rest of the line is marked with X.',
    defaultValue: true,
    enabledFor: (game) => game === 'mosaic',
  },
  autoCompleteClues: {
    name: 'Auto-complete clues',
    description:
      'When every cell in a line is filled or marked with X and the filled cells match the clue, the clue is ticked off for you.',
    defaultValue: false,
    enabledFor: (game) => game === 'mosaic',
  },
};

/** A setting's name as shown for `game`. Hide-hints is the one whose name depends on the game. */
export function settingName(key: SettingKey, game: Pick<GameMeta, 'enableHints'>): string {
  if (key === SettingsKeys.HIDE_HINT_BUTTONS && !game.enableHints) return 'Hide check button';
  return SettingsInfo[key].name;
}

/** The stored value if there is one, else the setting's own default. */
export function settingOrDefault(key: SettingKey, stored: unknown): any {
  return stored !== undefined ? stored : SettingsInfo[key].defaultValue;
}

export type SettingsBlob = Record<string, any>;

export interface SettingsStore {
  load: () => SettingsBlob | Promise<SettingsBlob>;
  save: (settings: SettingsBlob) => void | Promise<void>;
  /**
   * Optional hook for changes made outside this provider — another browser tab, say. A platform
   * with a single instance of the app has nothing to report and can leave it out.
   */
  subscribe?: (onChange: () => void) => () => void;
}

export interface SettingsContextType {
  settings: SettingsBlob;
  /** False until the store's first load lands; until then every setting reads as its default. */
  loaded: boolean;
  updateSetting: (key: string, value: any) => void;
  updateGameSetting: (game: GameType, key: string, value: any) => void;
  /** The stored value, or the setting's default from SettingsInfo. */
  getSetting: (key: SettingKey) => any;
  getGameSetting: (game: GameType, key: SettingKey) => any;
  refreshSettings: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ store, children }: PropsWithChildren<{ store: SettingsStore }>) {
  const [settings, setSettings] = useState<SettingsBlob>({});
  const [loaded, setLoaded] = useState(false);

  const refreshSettings = useCallback(() => {
    Promise.resolve(store.load()).then((blob) => {
      setSettings(blob);
      setLoaded(true);
    });
  }, [store]);

  useEffect(() => {
    refreshSettings();
    return store.subscribe?.(refreshSettings);
  }, [refreshSettings, store]);

  /*
    Writes update state from the value just written rather than re-reading the store. Re-reading
    would be a round trip on a platform whose storage is async, and the pending write would race it.
  */
  const commit = useCallback(
    (next: SettingsBlob) => {
      setSettings(next);
      Promise.resolve(store.save(next));
    },
    [store]
  );

  const updateSetting = useCallback(
    (key: string, value: any) => commit({ ...settings, [key]: value }),
    [commit, settings]
  );

  const updateGameSetting = useCallback(
    (game: GameType, key: string, value: any) =>
      commit({ ...settings, games: { ...settings.games, [game]: { ...settings.games?.[game], [key]: value } } }),
    [commit, settings]
  );

  const getSetting = useCallback((key: SettingKey) => settingOrDefault(key, settings[key]), [settings]);

  const getGameSetting = useCallback(
    (game: GameType, key: SettingKey) => settingOrDefault(key, settings.games?.[game]?.[key]),
    [settings]
  );

  return (
    <SettingsContext.Provider
      value={{ settings, loaded, updateSetting, updateGameSetting, getSetting, getGameSetting, refreshSettings }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within a SettingsProvider');
  return context;
};

/** A global setting, as a `[value, setValue]` pair. */
export const useSetting = (key: SettingKey) => {
  const { getSetting, updateSetting } = useSettings();
  return [getSetting(key), (value: any) => updateSetting(key, value)] as const;
};

/** A per-game setting, as a `[value, setValue]` pair. */
export const useGameSetting = (game: GameType, key: SettingKey) => {
  const { getGameSetting, updateGameSetting } = useSettings();
  return [getGameSetting(game, key), (value: any) => updateGameSetting(game, key, value)] as const;
};

/**
 * Calls `onTurnedOn` when a per-game setting goes from off to on while the caller is mounted — the
 * player switching it on, not a stored "on" arriving with the first load, which looks the same from
 * the value alone.
 */
export const useGameSettingTurnedOn = (game: GameType, key: SettingKey, onTurnedOn: () => void) => {
  const { loaded, getGameSetting } = useSettings();
  const on = getGameSetting(game, key) === true;
  const was = useRef<boolean | null>(null);
  useEffect(() => {
    const before = was.current;
    was.current = loaded ? on : null;
    if (before === false && on) onTurnedOn();
  }, [loaded, on, onTurnedOn]);
};
